import * as React from 'react';
import { v4 as uuidgen } from 'uuid';
import {
	StorageKeyBuilder,
	usePersistChangeInBrowserStorage,
} from '../../../../../models/hooks/usePersistChangeInBrowserStorage';
import { SavedMessage } from '../types/savedMessages';

export const useSavedMessages = (refreshKey?: number) => {
	// Build storage keys inside the hook so user session is available
	const messagesStorageKey = React.useMemo(
		() => StorageKeyBuilder.create('call-analysis-saved-messages').useUser().excludeFromClear().build(),
		[]
	);
	const lastUsedStorageKey = React.useMemo(
		() => StorageKeyBuilder.create('call-analysis-last-used-message').useUser().excludeFromClear().build(),
		[]
	);

	// Storage for messages list
	const [messagesState, setMessagesState] = usePersistChangeInBrowserStorage<SavedMessage[]>({
		storageType: 'localStorage',
		storageKey: messagesStorageKey,
		defaultIfEmpty: [],
	});

	// Storage for last used message ID
	const [lastUsedId, setLastUsedId] = usePersistChangeInBrowserStorage<string | null>({
		storageType: 'localStorage',
		storageKey: lastUsedStorageKey,
		defaultIfEmpty: null,
	});

	// Force re-read from localStorage when refreshKey changes
	React.useEffect(() => {
		if (refreshKey !== undefined) {
			const storedMessages = window.localStorage.getItem(messagesStorageKey);
			if (storedMessages) {
				try {
					setMessagesState(JSON.parse(storedMessages));
				} catch {
					// Ignore parse errors
				}
			}
			const storedLastUsed = window.localStorage.getItem(lastUsedStorageKey);
			if (storedLastUsed) {
				try {
					setLastUsedId(JSON.parse(storedLastUsed));
				} catch {
					// Ignore parse errors
				}
			}
		}
	}, [refreshKey, messagesStorageKey, lastUsedStorageKey, setMessagesState, setLastUsedId]);

	// CRUD operations
	const addMessage = React.useCallback(
		(label: string, content: string) => {
			const newMessage: SavedMessage = {
				id: uuidgen(),
				label,
				content,
				createdAt: Date.now(),
				order: messagesState?.length ?? 0,
			};
			setMessagesState([...(messagesState ?? []), newMessage]);
			return newMessage.id;
		},
		[messagesState, setMessagesState]
	);

	const updateMessage = React.useCallback(
		(id: string, label: string, content: string) => {
			if (!messagesState) return;
			setMessagesState(messagesState.map(msg => (msg.id === id ? { ...msg, label, content } : msg)));
		},
		[messagesState, setMessagesState]
	);

	const deleteMessage = React.useCallback(
		(id: string) => {
			if (!messagesState) return;
			setMessagesState(messagesState.filter(msg => msg.id !== id));
			// Clear last used if deleting that message
			if (lastUsedId === id) {
				setLastUsedId(null);
			}
		},
		[messagesState, setMessagesState, lastUsedId, setLastUsedId]
	);

	const reorderMessages = React.useCallback(
		(reorderedMessages: SavedMessage[]) => {
			// Update order property for each message
			const withUpdatedOrder = reorderedMessages.map((msg, index) => ({
				...msg,
				order: index,
			}));
			setMessagesState(withUpdatedOrder);
		},
		[setMessagesState]
	);

	const getMessageById = React.useCallback(
		(id: string) => {
			return messagesState?.find(msg => msg.id === id);
		},
		[messagesState]
	);

	const setLastUsed = React.useCallback(
		(id: string | null) => {
			setLastUsedId(id);
		},
		[setLastUsedId]
	);

	// Sort messages by order
	const sortedMessages = React.useMemo(() => {
		if (!messagesState) return [];
		return [...messagesState].sort((a, b) => a.order - b.order);
	}, [messagesState]);

	return {
		messages: sortedMessages,
		lastUsedId,
		addMessage,
		updateMessage,
		deleteMessage,
		reorderMessages,
		getMessageById,
		setLastUsed,
	};
};
